import { useEffect, useState, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useScrollLock } from '../hooks/useScrollLock'
import type { GenerationCard } from '../types/generationCard'
import { buildOfficeViewerUrl } from '../utils/presentationCard'
import { Icon } from '../../public/icons/Icon.tsx'
import SideTooltip from './SideTooltip'
import { CreateVideoIcon } from './CreateVideoIcon'
import { showToast } from './Toast'
import MediaPlayer from './MediaPlayer'
import ModelIcon from './ModelIcon'
import { describeMusicModel } from '../data/musicModels'
import { coverGradient } from '../data/coverGradient'
import SunoActionsSheet from '../pages/generate/components/SunoActionsSheet'
import type { SunoTrack } from '../api/music'
import type { AudioOperation } from '../context/GenerateContext'
import { useSunoCoverIndex } from '../context/SunoCoverIndexContext'
import { useSunoWavJobs } from '../context/SunoWavJobsContext'
import { useSunoMidiJobs } from '../context/SunoMidiJobsContext'
import { downloadMp3 } from '../pages/bots/utils/downloadSunoTrack'
import { downloadAudioWithCover, safeFilename } from '../pages/bots/utils/audioWithCover'
import { rateGeneration } from '../api/images'
import { fileTypeLabel } from '../utils/fileType'
import { blobExtension, fetchOriginalBlob } from '../utils/downloadOriginal'

interface Props {
	card: GenerationCard
	onClose: () => void
	onPrimaryAction?: (card: GenerationCard) => void
	onCreateVideo?: (card: GenerationCard) => void
	/** Начальная позиция воспроизведения для audio (resume из inline preview).
	 *  Применяется только к первому треку — остальные треки начинаются с 0. */
	initialTime?: number
	/** Вызывается когда нужно обновить источник данных (например, после Suno-действия,
	 *  создающего новый трек в библиотеке — WAV, стемы, cover и т.п.). */
	onRefresh?: () => void
	/** Переключает виджет в режим параметрической операции Suno
	 *  (extend/cover/add_vocals/add_instrumental). Если не передан — блок скрыт. */
	onTrackParametricAction?: (track: SunoTrack, operation: AudioOperation) => void
	/** Сырая запись трека из БД — нужна для `SunoActionsSheet` и всех его
	 *  вложенных операций (он ожидает полный SunoTrack, а не GenerationCard). */
	sourceTrack?: SunoTrack | null
	/** Список всех треков пользователя — передаётся в SunoMashupSheet чтобы
	 *  не делать лишний getUserTracks на каждое открытие. Опционально. */
	tracksCache?: SunoTrack[]
	/** Оценка поставлена (карточка истории с requestId) — родитель персистит
	 *  её в запись (updateGeneration), чтобы не сбрасывалась между открытиями. */
	onRated?: (card: GenerationCard, rating: number) => void
	/** Соседние работы для ленты миниатюр под превью + перелистывания ←/→ и
	 *  свайпом. Передаёт «Мои генерации» (/files): там модал заменил прежний
	 *  лайтбокс, и терять навигацию по ленте было нельзя. Не передан —
	 *  ленты нет, поведение как раньше (студия, кабинет, галереи). */
	siblings?: GenerationCard[]
	/** Выбрана другая работа из ленты — родитель меняет открытую карточку. */
	onSelectSibling?: (card: GenerationCard) => void
	/** Готовый WAV трека, когда `sourceTrack` не передан (лента /files знает
	 *  wav_url из бэка, но полной записи SunoTrack у неё нет). */
	wavUrl?: string | null
	/** Своя реализация «Скачать» — у /files она умеет iOS Share и отдачу
	 *  через бэк с Content-Disposition. Не передана — штатный blob-download. */
	onDownload?: (card: GenerationCard) => void
}

const SETTING_ICONS: Record<string, string> = {
	'Aspect ratio': 'gen-aspect-ratio',
	'Duration':     'gen-duration',
	'Resolution':   'gen-resolution',
}


export default function CardModal({ card, onClose, onPrimaryAction, onCreateVideo, initialTime, onRefresh, onTrackParametricAction, sourceTrack, tracksCache, onRated, siblings, onSelectSibling, wavUrl, onDownload }: Props) {
	const [copied, setCopied] = useState(false)
	const [expanded, setExpanded] = useState(false)
	const [closing, setClosing] = useState(false)
	const [actionsSheetOpen, setActionsSheetOpen] = useState(false)
	// Оценка генерации: доступна только когда запись несёт requestId (пишется
	// в историю с 2026-07-29 — у старых записей кнопок нет, врать нечем).
	const [rating, setRating] = useState<number | null>(card.userRating ?? null)
	const handleRate = useCallback(async (r: number) => {
		if (!card.requestId) return
		try {
			await rateGeneration(card.requestId, r)
			setRating(r)
			onRated?.(card, r)
		} catch { /* оценка не критична — тихо игнорируем сетевой сбой */ }
	}, [card, onRated])
	const { getCover } = useSunoCoverIndex()
	// Генерация из tool-режима карточки (промпт системный, скрыт от юзера):
	// не показываем ни промпт, ни модель — только параметры фото и действия.
	const hideMeta = card.type === 'generation' && !!card.promptLocked

	// Эффективный URL обложки: сохранённый previewUrl → резолв через индекс
	// Suno по первому audio URL. Для не-audio просто card.previewUrl.
	const effectiveCoverUrl = (() => {
		if (card.previewUrl) return card.previewUrl
		if (card.kind !== 'audio') return ''
		const firstAudio = card.audioTracks?.[0]
		return getCover(firstAudio) ?? ''
	})()

	// WAV url: backend-поле → localStorage-кэш jobs-контекста (для случая когда
	// backend ещё не деплой не отдаёт wav_url). Конвертацию ЗДЕСЬ не запускаем —
	// только скачивание уже готового. Запуск конвертации только через sheet.
	const wavJobs = useSunoWavJobs()
	// sourceTrack есть у ленты треков; у /files его нет — там wav_url приезжает
	// отдельным полем бэка. Порядок: живой трек → проп → ничего.
	const effectiveWavUrl = wavJobs.resolveWav(sourceTrack) || wavUrl || null
	const wavConverting = !!sourceTrack && wavJobs.isRunning(sourceTrack.id)

	// MIDI — данные в localStorage-кэше. Если есть — показываем кнопку «MIDI»
	// в формате, открывающую viewer через openCached.
	const midiJobs = useSunoMidiJobs()
	const midiCached = sourceTrack ? midiJobs.getCached(sourceTrack.id) : null

	const handleRecheckWav = () => {
		if (sourceTrack) wavJobs.recheck(sourceTrack.id)
	}

	const handleMp3 = () => {
		if (sourceTrack) {
			// Full embed-flow — cover + metadata берутся из sourceTrack.
			downloadMp3(sourceTrack)
			return
		}
		// Fallback: нет sourceTrack (виджет после генерации). Встраиваем
		// обложку из card.previewUrl если это реально картинка.
		if (card.audioTracks?.[0]) {
			const coverUrl = effectiveCoverUrl && !/\.(mp3|wav|m4a|ogg|flac)(\?|$)/i.test(effectiveCoverUrl)
				? effectiveCoverUrl
				: null
			downloadAudioWithCover(
				card.audioTracks[0],
				coverUrl,
				safeFilename(card.audioTitle, 'mp3'),
				{ title: card.audioTitle, artist: 'Umnik AI' },
			)
		}
	}
	const handleWavDownloadOnly = () => {
		if (!effectiveWavUrl) return
		const title = sourceTrack?.title || card.audioTitle || null
		const coverUrl = sourceTrack?.image_url
			|| (effectiveCoverUrl && !/\.(mp3|wav|m4a|ogg|flac)(\?|$)/i.test(effectiveCoverUrl) ? effectiveCoverUrl : null)
		downloadAudioWithCover(
			effectiveWavUrl,
			coverUrl,
			safeFilename(title, 'wav'),
			{
				title,
				artist: 'Umnik AI',
				genre: sourceTrack?.tags,
			},
		)
	}

	const handleClose = useCallback((callback?: () => void) => {
		setClosing(true)
		setTimeout(() => {
			if (typeof callback === 'function') callback()
			onClose()
		}, 360)
	}, [onClose])

	const handleCopyPrompt = useCallback(() => {
		// У скрытого промпта копировать можно только СВОЙ текст — промпта
		// инструмента на клиенте нет.
		const text = hideMeta ? card.userPrompt : card.prompt
		if (!text) return
		navigator.clipboard.writeText(text).then(() => {
			setCopied(true)
			setTimeout(() => setCopied(false), 1800)
		})
	}, [card.prompt, card.userPrompt, hideMeta])

	// Перелистывание соседей: индекс ищем по id — родитель волен пересобирать
	// массив (подгрузка следующей страницы ленты), ссылки на объекты не держим.
	const navigateSibling = useCallback(
		(dir: 1 | -1) => {
			if (!siblings || siblings.length < 2 || !onSelectSibling) return
			const i = siblings.findIndex((s) => s.id === card.id)
			if (i === -1) return
			const next = siblings[(i + dir + siblings.length) % siblings.length]
			if (next) onSelectSibling(next)
		},
		[siblings, onSelectSibling, card.id],
	)

	useEffect(() => {
		function handleKey(e: KeyboardEvent) {
			if (e.key === 'Escape') handleClose()
			if (e.key === 'ArrowLeft') navigateSibling(-1)
			if (e.key === 'ArrowRight') navigateSibling(1)
		}
		document.addEventListener('keydown', handleKey)
		return () => document.removeEventListener('keydown', handleKey)
	}, [handleClose, navigateSibling])

	// Активную миниатюру держим в поле зрения — иначе после пары стрелок лента
	// «уезжает» и юзер не понимает, где он.
	const stripRef = useRef<HTMLDivElement>(null)
	useEffect(() => {
		const active = stripRef.current?.querySelector('.rg-modal__strip-item--active')
		;(active as HTMLElement | null)?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' })
	}, [card.id])
	const swipeStartX = useRef(0)

	// Лок скролла С СОХРАНЕНИЕМ позиции — иначе при открытии страница теряет
	// scroll-высоту (body height:100% + overflow:hidden), прыгает наверх, и
	// после закрытия юзер не на том месте списка, откуда открыл трек.
	useScrollLock(true)

	const downloadFile = async (url: string, filenameHint?: string) => {
		try {
			const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent)
			if (isIOS) showToast('Подготовка файла...', 'info')

			const blob = await fetchOriginalBlob(url)
			const ext = blobExtension(blob)
			const filename = filenameHint || `generation-${card.id}.${ext}`

			if (isIOS) {
				const shareFile = new File([blob], filename, { type: blob.type })
				if (navigator.canShare?.({ files: [shareFile] })) {
					try {
						await navigator.share({ files: [shareFile] })
					} catch (e) {
						if (e instanceof Error && e.name !== 'AbortError') throw e
					}
					return
				}
				window.open(url, '_blank')
				return
			}

			const objectUrl = URL.createObjectURL(blob)
			const a = document.createElement('a')
			a.href = objectUrl
			a.download = filename
			document.body.appendChild(a)
			a.click()
			a.remove()
			setTimeout(() => URL.revokeObjectURL(objectUrl), 10000)
		} catch {
			showToast('Не удалось скачать файл', 'error')
		}
	}

	const handleDownload = async () => {
		// Родитель умеет скачивать лучше нас (iOS Share, отдача через бэк) —
		// отдаём ему.
		if (onDownload) {
			onDownload(card)
			return
		}
		// Для audio — скачиваем первый трек; если нужны оба, есть отдельные кнопки внутри плееров
		if (card.kind === 'audio' && card.audioTracks && card.audioTracks.length > 0) {
			await downloadFile(card.audioTracks[0], `${card.audioTitle || 'track'}.mp3`)
			return
		}
		// Презентация: PPTX качаем ЯКОРЕМ по presigned URL (как делал старый
		// модал) — навигационная загрузка не зависит от CORS бакета, в отличие
		// от fetch→blob ниже. previewUrl тут — PNG первого слайда, не файл.
		if (card.kind === 'presentation' && card.pptxUrl) {
			const a = document.createElement('a')
			a.href = card.pptxUrl
			a.download = card.presentationMode === 'create' ? 'presentation.pptx' : 'presentation_edited.pptx'
			a.target = '_blank'
			a.rel = 'noopener noreferrer'
			document.body.appendChild(a)
			a.click()
			a.remove()
			return
		}
		// Карточки бэк-ленты: previewUrl — лёгкое webp, оригинал в fullUrl.
		await downloadFile(card.fullUrl || card.previewUrl)
	}

	const isStyle = card.type === 'style'
	const hasStrip = !!siblings && siblings.length > 1 && !!onSelectSibling
	const isAudioCard = card.kind === 'audio'
	const isPresentationCard = card.kind === 'presentation'
	// Для аудио — конкретная музыкальная модель (логотип + имя + версия)
	// вместо сырого id 'mureka-8'. null → нет осмысленной модели (старый трек)
	// → fallback на card.modelName.
	const musicModel = isAudioCard ? describeMusicModel(card.modelId) : null

	const vocalLabel = (v?: string) => v === 'm' ? 'Муж' : v === 'f' ? 'Жен' : 'Авто'

	const primaryLabel = isStyle
		? 'Применить стиль'
		: hideMeta
		// «Сделать ещё», а не «Открыть инструмент»: кнопка уводит на страницу
		// инструмента, которым сделана генерация, и человек делает там ещё одну.
		// Ни промпт, ни настройки не подставляются — при скрытом промпте
		// подставлять нечего, бэк его не отдаёт. Прежняя подпись была вдвое
		// длиннее и в компактном ряду на телефоне переносилась в две строки.
		// «Повторить» занято повтором после ошибки, «Воссоздать» — соседней
		// веткой, где настройки как раз подставляются.
		? 'Сделать ещё'
		: 'Воссоздать'

	const otherSettings = isAudioCard
		? ([
			card.audioStyle ? { label: 'Стиль', value: card.audioStyle } : null,
			!card.audioInstrumental
				? { label: 'Вокал', value: vocalLabel(card.audioVocalGender) }
				: null,
			{ label: 'Инструментал', value: card.audioInstrumental ? 'Да' : 'Нет' },
			{ label: 'Кастомный режим', value: card.audioCustomMode ? 'Да' : 'Нет' },
			// «Формат» показываем отдельным блоком ниже вместе с download-кнопками,
			// не как inline строку — это более действенное место для UX.
		].filter(Boolean) as { label: string; value: string }[])
		: isPresentationCard
		? ([
			{ label: 'Режим', value: card.presentationMode === 'create' ? 'Создание с нуля' : 'Редактирование' },
			{ label: 'Формат', value: 'PPTX · Microsoft PowerPoint' },
		] as { label: string; value: string }[])
		: ([
			card.settings?.aspectRatio
				? { label: 'Aspect ratio', value: card.settings.aspectRatio }
				: null,
			card.isVideo && card.settings?.duration
				? { label: 'Duration', value: `${card.settings.duration} sec` }
				: null,
			card.settings?.resolution
				? { label: 'Resolution', value: card.settings.resolution }
				: null,
			{
				label: 'File type',
				// По самому файлу: заказ и результат расходятся, если провайдер формат не выполнил (владелец 26.09).
				value: card.isVideo ? 'MP4' : fileTypeLabel(card.fullUrl || card.previewUrl, card.settings?.outputFormat) || 'JPG',
			},
		].filter(Boolean) as { label: string; value: string }[])

	return createPortal(
		<div className={`rg-modal-backdrop${closing ? ' rg-modal-backdrop--closing' : ''}`} onClick={() => handleClose()}>
			<div className={`rg-modal${closing ? ' rg-modal--closing' : ''}`} onClick={(e) => e.stopPropagation()}>

				{/* Кнопка закрытия над изображением — только мобилка */}
				<button
					className="rg-modal__close-overlay"
					onClick={() => handleClose()}
					type="button"
					aria-label="Закрыть"
				>
					<Icon id="gen-close" size={11} />
				</button>

				{/* ── Превью слева (+ лента соседей, если их передали) ── */}
				<div
					className={`rg-modal__media-col${hasStrip ? ' rg-modal__media-col--strip' : ''}`}
					onTouchStart={(e) => { swipeStartX.current = e.touches[0].clientX }}
					onTouchEnd={(e) => {
						const dx = e.changedTouches[0].clientX - swipeStartX.current
						if (Math.abs(dx) > 50) navigateSibling(dx < 0 ? 1 : -1)
					}}
				>
				<div className="rg-modal__media">
					{isPresentationCard ? (
						card.pptxUrl ? (
							/* Живой просмотр слайдов: Office Online Viewer по presigned
							   PPTX. Внутрь iframe CSS не проникает (cross-origin) —
							   верхнюю служебную полосу MS обрезаем клип-контейнером. */
							<div className="rg-modal__media-pres-frame">
								<iframe
									src={buildOfficeViewerUrl(card.pptxUrl)}
									className="rg-modal__media-pres-iframe"
									title={card.title || 'Презентация'}
									allow="fullscreen"
									loading="eager"
								/>
							</div>
						) : (
							/* PPTX недоступен (MinIO down / URL истёк) — заглушка. */
							<div className="rg-modal__media-pres">
								<div className="rg-modal__media-pres-icon" aria-hidden="true">📑</div>
								<div className="rg-modal__media-pres-title">
									{card.presentationMode === 'create'
										? 'Презентация создана'
										: 'Презентация отредактирована'}
								</div>
								<div className="rg-modal__media-pres-subtitle">
									PowerPoint · PPTX
								</div>
							</div>
						)
					) : card.kind === 'audio' ? (
						<div className="rg-modal__media-audio">
							{effectiveCoverUrl && !effectiveCoverUrl.endsWith('.mp3') && !effectiveCoverUrl.endsWith('.wav') ? (
								<div className="rg-modal__media-audio-cover-wrap">
								<img
									src={effectiveCoverUrl}
									alt={card.audioTitle || card.prompt}
									className="rg-modal__media-audio-cover"
								/>
								<button
									type="button"
									className="rg-modal__media-audio-cover-dl"
									title="Скачать обложку"
									onClick={(e) => {
										e.stopPropagation()
										downloadFile(effectiveCoverUrl, `${card.audioTitle || 'cover'}.jpg`)
									}}
								>
									<Icon id="gen-download" size={16} />
								</button>
							</div>
							) : (
								<div
									className="rg-modal__media-audio-placeholder"
									style={{ background: coverGradient(card.sunoTrackId || card.id) }}
								>
									<Icon id="gen-audio" size={72} />
								</div>
							)}
							<div className="rg-modal__media-audio-tracks">
								{(card.audioTracks && card.audioTracks.length > 0
									? card.audioTracks
									: [card.previewUrl]
								).filter(Boolean).map((url, idx, arr) => (
									<MediaPlayer
										key={idx}
										variant="audio"
										src={url}
										title={arr.length > 1 ? `Трек ${idx + 1}` : (card.audioTitle || 'Аудио трек')}
										// Resume с inline-preview позиции — только для первого трека
										initialTime={idx === 0 ? initialTime : undefined}
										// Автоплей первого трека — чтобы resume воспроизвёлся сразу
										autoPlay={idx === 0 && typeof initialTime === 'number' && initialTime > 0}
										// startMuted ТОЛЬКО вместе с автоплеем: флаг глушит
										// элемент при монтировании, и на треке, который юзер
										// запускает сам, это дало бы немое воспроизведение
										startMuted={idx === 0 && typeof initialTime === 'number' && initialTime > 0}
										onDownload={() => downloadFile(
											url,
											`${card.audioTitle || 'track'}${arr.length > 1 ? `-${idx + 1}` : ''}.mp3`,
										)}
										className="rg-modal__media-player rg-modal__media-player--audio"
									/>
								))}
							</div>
						</div>
					) : card.isVideo ? (
						<MediaPlayer
							variant="video"
							src={card.fullUrl || card.previewUrl}
							autoPlay
							// Автозапуск уважает настройку аккаунта «автоматически
							// включать звук»: без неё видео стартует беззвучно и не
							// застаёт врасплох (раньше флага не было — звучало всегда)
							startMuted
							loop
							onDownload={handleDownload}
							className="rg-modal__media-player rg-modal__media-player--video"
						/>
					) : (
						<img
							src={card.fullUrl || card.previewUrl}
							alt={card.title || card.prompt}
							className="rg-modal__media-el"
						/>
					)}
				</div>

				{hasStrip && (
					<div className="rg-modal__strip" ref={stripRef}>
						{siblings!.map((s) => (
							<button
								key={s.id}
								type="button"
								className={`rg-modal__strip-item${s.id === card.id ? ' rg-modal__strip-item--active' : ''}`}
								onClick={() => onSelectSibling!(s)}
								aria-label={s.title || s.audioTitle || 'Работа'}
							>
								{s.previewUrl ? (
									<img src={s.previewUrl} alt="" loading="lazy" />
								) : (
									<span className="rg-modal__strip-fallback" aria-hidden="true">
										<Icon id={s.kind === 'audio' ? 'gen-audio' : 'gen-image'} size={16} />
									</span>
								)}
							</button>
						))}
					</div>
				)}
				</div>

				{/* ── Панель справа ── */}
				<div className={`rg-modal__panel${expanded ? ' rg-modal__panel--expanded' : ''}`}>
					{/* Toggle — handle зона, только мобилка */}
					<button
						className="rg-modal__panel-toggle"
						onClick={() => setExpanded(v => !v)}
						type="button"
						aria-label={expanded ? 'Свернуть' : 'Развернуть'}
					>
						<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
							{expanded
								? <polyline points="6,9 12,15 18,9" />
								: <polyline points="18,15 12,9 6,15" />
							}
						</svg>
					</button>

					{/* Хедер */}
					<div className="rg-modal__header">
						{isStyle ? (
							<span className="rg-modal__style-title">{card.title}</span>
						) : isAudioCard ? (
							// Для audio — две кнопки только для доступных форматов.
							// MP3 доступен всегда когда есть audioTracks. WAV — только
							// если трек был сконвертирован (sourceTrack.wav_url).
							// Эти кнопки НЕ запускают конвертацию — только скачивание.
							// Для конвертации есть отдельный flow в «Работать с треком».
							<div className="rg-modal__downloads">
								{card.audioTracks?.[0] && (
									<button
										className="rg-modal__download"
										onClick={handleMp3}
										type="button"
										title="Скачать MP3"
									>
										<Icon id="gen-download" size={13} />
										Скачать MP3
									</button>
								)}
								{effectiveWavUrl ? (
									<button
										className="rg-modal__download rg-modal__download--wav"
										onClick={handleWavDownloadOnly}
										type="button"
										title="Скачать WAV (lossless)"
									>
										<Icon id="gen-download" size={13} />
										Скачать WAV
									</button>
								) : wavConverting ? (
									<button
										type="button"
										className="rg-modal__download rg-modal__download--wav-converting"
										onClick={handleRecheckWav}
										title="Идёт конвертация. Клик — проверить сейчас"
									>
										<span className="rg-modal__download-spinner" aria-hidden="true" />
										WAV конвертируется…
									</button>
								) : null}
								{midiCached && sourceTrack && (
									<button
										type="button"
										className="rg-modal__download rg-modal__download--midi"
										onClick={() => midiJobs.openCached(sourceTrack.id, sourceTrack.title)}
										title="Открыть MIDI viewer (скачать .mid)"
									>
										<Icon id="gen-download" size={13} />
										Открыть MIDI
									</button>
								)}
							</div>
						) : isPresentationCard ? (
							// Медиа-зона занята живым вьювером слайдов — скачивание
							// PPTX той же панельной кнопкой, что у остальных типов.
							card.pptxUrl ? (
								<button className="rg-modal__download" onClick={handleDownload} type="button">
									<Icon id="gen-download" size={13} />
									Скачать
								</button>
							) : null
						) : (
							<button className="rg-modal__download" onClick={handleDownload} type="button">
								<Icon id="gen-download" size={13} />
								Скачать
							</button>
						)}
					<button
							className="rg-modal__close"
							onClick={onClose}
							type="button"
							aria-label="Закрыть"
						>
							<Icon id="gen-close" size={11} />
						</button>
					</div>

					{/* Контент */}
					<div className="rg-modal__content">

						{/* Название трека — только для audio */}
						{isAudioCard && card.audioTitle && (
							<div className="rg-modal__section">
								<div className="rg-modal__section-title">Название трека</div>
								<p className="rg-modal__prompt">{card.audioTitle}</p>
							</div>
						)}

						{/* Исходное фото — только для image/video */}
						{!isStyle && !isAudioCard && !isPresentationCard && card.sourceImageUrl && (
							<div className="rg-modal__section">
								<div className="rg-modal__section-title">Исходное изображение</div>
								<img
									src={card.sourceImageUrl}
									alt="Source"
									className="rg-modal__source-img"
								/>
							</div>
						)}

						{/* Промпт инструмента (promptLocked) — наш, его тут нет вовсе:
						    бэк отдаёт card.prompt пустым. Показываем ТОЛЬКО то, что юзер
						    дописал сам, и поясняем, почему остального не видно. Модель
						    ниже остаётся скрытой — по ней инструмент тоже воспроизводим. */}
						{hideMeta && (
							<div className="rg-modal__section">
								<div className="rg-modal__section-title">
									Промпт
									{card.userPrompt && (
										<SideTooltip text="Копировать промпт">
											<button
												className={`rg-modal__copy-btn${copied ? ' rg-modal__copy-btn--done' : ''}`}
												onClick={handleCopyPrompt}
												type="button"
											>
												<Icon id={copied ? 'gen-check' : 'gen-copy'} size={12} />
											</button>
										</SideTooltip>
									)}
								</div>
								{card.userPrompt && <p className="rg-modal__prompt">{card.userPrompt}</p>}
								<p className="rg-modal__prompt rg-modal__prompt--empty">
									{card.userPrompt
										? 'Показан только ваш текст — остальное задаёт готовый инструмент'
										: 'Промпт задаёт готовый инструмент и не отображается'}
									{card.toolCard?.title ? ` («${card.toolCard.title}»)` : ''}
								</p>
							</div>
						)}
						{!hideMeta && (
							<div className="rg-modal__section">
								<div className="rg-modal__section-title">
									Промпт
									{card.prompt && (
										<SideTooltip text="Копировать промпт">
											<button
												className={`rg-modal__copy-btn${copied ? ' rg-modal__copy-btn--done' : ''}`}
												onClick={handleCopyPrompt}
												type="button"
											>
												<Icon id={copied ? 'gen-check' : 'gen-copy'} size={12} />
											</button>
										</SideTooltip>
									)}
								</div>
								{card.prompt ? (
									<p className="rg-modal__prompt">{card.prompt}</p>
								) : (
									<p className="rg-modal__prompt rg-modal__prompt--empty">Без промпта</p>
								)}
							</div>
						)}

						{/* Единый primary CTA «Улучшить» — открывает шторку с
						    группированными AI-операциями. Sparkles-иконка совпадает
						    с кнопкой «Улучшить» на строке в SunoTrackList — один
						    визуальный язык для одного действия. */}
						{isAudioCard && sourceTrack && (
							<div className="rg-modal__section">
								<button
									type="button"
									className="suno-work-cta"
									onClick={() => setActionsSheetOpen(true)}
								>
									<span className="suno-work-cta__icon" aria-hidden="true">
										<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
											<path d="M12 3l1.6 5L19 10l-5.4 1.6L12 17l-1.6-5.4L5 10l5.4-1.6L12 3z" />
											<path d="M18 16l.9 2.5 2.5.9-2.5.9L18 23l-.9-2.7-2.5-.9 2.5-.9L18 16z" />
										</svg>
									</span>
									<span className="suno-work-cta__text">
										<span className="suno-work-cta__label">Улучшить</span>
										<span className="suno-work-cta__desc">
											WAV · Стемы · MIDI · Обложка · Продлить · Cover · + Вокал · ещё
										</span>
									</span>
									<span className="suno-work-cta__arrow" aria-hidden="true">→</span>
								</button>
							</div>
						)}

						{/* Оценка результата — только у генераций с requestId (история
						    пишет его с 2026-07-29; старым записям привязать оценку не к
						    чему — блока нет). */}
						{card.type === 'generation' && card.requestId && (
							<div className="rg-modal__section">
								<div className="rg-modal__section-title">Оценка</div>
								<div className="rg-modal__rate-row">
									<button
										type="button"
										className={`rg-modal__rate-btn${rating === 1 ? ' rg-modal__rate-btn--on' : ''}`}
										title="Нравится"
										style={{ opacity: rating === -1 ? 0.35 : 1 }}
										onClick={() => handleRate(1)}
									>
										<Icon id={rating === 1 ? 'gen-thumbs-up-filled' : 'gen-thumbs-up'} size={15} color={rating === 1 ? '#22c55e' : 'currentColor'} />
									</button>
									<button
										type="button"
										className={`rg-modal__rate-btn${rating === -1 ? ' rg-modal__rate-btn--on' : ''}`}
										title="Не нравится"
										style={{ opacity: rating === 1 ? 0.35 : 1 }}
										onClick={() => handleRate(-1)}
									>
										<Icon id={rating === -1 ? 'gen-thumbs-down-filled' : 'gen-thumbs-down'} size={15} color={rating === -1 ? '#ef4444' : 'currentColor'} />
									</button>
									<span className="rg-modal__rate-hint">Оценка помогает нам отбирать лучшие модели</span>
								</div>
							</div>
						)}

						{/* Настройки */}
						<div className="rg-modal__section">
							<div className="rg-modal__section-title">Настройки</div>

							{/* Модель — полная строка. Для аудио: mini-логотип
							    нейросети + конкретная модель с версией. Скрыта для
							    tool-режима карточек (promptLocked) — как и промпт. */}
							{!hideMeta && (
								<div className="rg-modal__model-row">
									<span className="rg-modal__setting-label">Модель</span>
									<span className="rg-modal__setting-value rg-modal__setting-value--model">
										{musicModel ? (
											<>
												<ModelIcon
													icon={musicModel.icon}
													name={musicModel.providerName}
													size={16}
													className="rg-modal__model-logo"
												/>
												{musicModel.name}
											</>
										) : (
											card.modelName
										)}
									</span>
								</div>
							)}

							{/* Остальные параметры */}
							{otherSettings.length > 0 && (
								<div className="rg-modal__settings">
									{otherSettings.map((s) => (
										<div key={s.label} className="rg-modal__setting">
											<span className="rg-modal__setting-label">{s.label}</span>
											<span className="rg-modal__setting-value">
												{SETTING_ICONS[s.label] && (
													<Icon id={SETTING_ICONS[s.label]} size={11} />
												)}
												{s.value}
											</span>
										</div>
									))}
								</div>
							)}

							{/* Срок хранения файла — честный lifecycle: результат живёт в
							    S3 ограниченно (видео 7 дн, фото 21 — RETENTION_DAYS бэка),
							    и юзер должен узнать об этом ДО того, как файл пропал.
							    Только для генераций с датой; kind audio/presentation —
							    свои разделы со своим показом срока. */}
							{card.type === 'generation' && card.createdAt && card.expiresAt !== null &&
								(card.kind === 'image' || card.kind === 'video' || (!card.kind && !card.isVideo)) && (() => {
								// Срок с сервера, когда он известен: у основ карточки товара он продлевается, у шаблонов
								// его нет (null — строку не показываем). Иначе — общий срок от создания.
								const days = card.kind === 'video' || card.isVideo ? 7 : 21
								const serverExpiry = card.expiresAt ? Date.parse(card.expiresAt) : NaN
								const expiresAt = Number.isNaN(serverExpiry) ? card.createdAt + days * 86400000 : serverExpiry
								const left = Math.ceil((expiresAt - Date.now()) / 86400000)
								const dateStr = new Date(expiresAt).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })
								return (
									<div className="rg-modal__setting">
										<span className="rg-modal__setting-label">Хранение</span>
										<span className={`rg-modal__setting-value${left <= 3 ? ' rg-modal__setting-value--warn' : ''}`}>
											{left <= 0
												? 'срок истёк — файл мог быть удалён'
												: `до ${dateStr} · скачайте, чтобы не потерять`}
										</span>
									</div>
								)
							})()}

							{/* Для audio — просто перечисление реально доступных форматов.
							    Скачивание — через кнопки вверху модала. Конвертацию
							    WAV юзер запускает через «Работать с треком» (если ещё
							    не конвертировал). */}
							{isAudioCard && (card.audioTracks?.[0] || effectiveWavUrl || midiCached) && (
								<div className="rg-modal__setting">
									<span className="rg-modal__setting-label">Формат</span>
									<span className="rg-modal__setting-value">
										{[
											card.audioTracks?.[0] ? 'MP3' : null,
											effectiveWavUrl ? 'WAV' : null,
											midiCached ? 'MIDI' : null,
										].filter(Boolean).join(', ')}
									</span>
								</div>
							)}
						</div>

					</div>

					{/* Footer */}
					<div className="rg-modal__footer">
						{onPrimaryAction && (
							// Скрытый промпт: подставлять в бар нечего, поэтому повтор =
							// переход на сам инструмент. Решение о переходе принимает
							// родитель (useGenerationRepeat), тут только честная подпись.
							<SideTooltip text={primaryLabel} delay={0}>
								<button
									className="rg-modal__recreate"
									onClick={() => { handleClose(() => onPrimaryAction(card)) }}
									type="button"
								>
									<Icon id="gen-reuse" size={15} />
									{primaryLabel}
								</button>
							</SideTooltip>
						)}
						{!card.isVideo && card.kind !== 'audio' && card.kind !== 'presentation' && onCreateVideo && (
							<SideTooltip text="Создать видео из этой генерации" delay={0}>
								<button
									className="rg-modal__create-video"
									onClick={() => { handleClose(() => onCreateVideo(card)) }}
									type="button"
								>
									<CreateVideoIcon size={15} />
									Создать видео
								</button>
							</SideTooltip>
						)}
					</div>

				</div>
			</div>

			{/* Sheet для Replace Section — монтируется внутри того же portal,
			    открывается поверх модала. Закрытие не закрывает CardModal. */}
			{isAudioCard && sourceTrack && (
				<SunoActionsSheet
					open={actionsSheetOpen}
					onClose={() => setActionsSheetOpen(false)}
					track={sourceTrack}
					onTrackParametricAction={
						onTrackParametricAction
							? (track, op) => {
									onTrackParametricAction(track, op)
									// Закрываем и шторку действий, и сам CardModal — юзер
									// переводится в виджет, карточка больше не нужна.
									setActionsSheetOpen(false)
									handleClose()
								}
							: undefined
					}
					onCompleted={onRefresh}
					tracksCache={tracksCache}
				/>
			)}
		</div>,
		document.body,
	)
}
